import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { entitlementsStateFromCapabilities } from "@/lib/capabilitiesAdapters";
import { invalidateCapabilitiesCache, useCapabilities } from "@/lib/useCapabilities";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { usePlatformDesignerBuyerPreview } from "@/lib/usePlatformDesignerBuyerPreview";
import { usePlatformDesignerBuyerPreviewOrg } from "@/lib/usePlatformDesignerBuyerPreviewOrg";
import {
  entitlementsFromSystemInfo,
  hasEntitlement,
} from "@/modules/entitlementsFromSystemInfo";
import { isWorkModuleEntitled } from "@/modules/catalogModulos";

/** Permisos totales — platform_designer (servidor: is_platform_designer). */
export const FULL_ENTITLEMENTS_STATE = {
  entitlements: { visor: true, laboral: true, tramites: true, despacho: true },
  modulo_visor_sat: true,
  modulo_asistente_laboral: true,
  modulo_tramites_fiscales: true,
  modulo_gestion_despacho: true,
  loading: false,
  source: "platform_designer",
};

let _sharedCache = { data: null, promise: null, fetchedAt: 0 };
const CACHE_TTL_MS = 30_000;

async function fetchSystemInfoShared() {
  const now = Date.now();
  if (_sharedCache.data && now - _sharedCache.fetchedAt < CACHE_TTL_MS) {
    return _sharedCache.data;
  }
  if (!_sharedCache.promise) {
    _sharedCache.promise = api
      .get("/system/info", { timeout: 60000 })
      .then(({ data }) => {
        _sharedCache.data = data;
        _sharedCache.fetchedAt = Date.now();
        _sharedCache.promise = null;
        return data;
      })
      .catch(() => {
        _sharedCache.promise = null;
        return null;
      });
  }
  return _sharedCache.promise;
}

/** Invalida caché tras instalar licencia o cambios en Configuración. */
export function invalidateEntitlementsCache() {
  _sharedCache = { data: null, promise: null, fetchedAt: 0 };
  invalidateCapabilitiesCache();
}

/**
 * Política de carga: system/info siempre para usuarios no diseñador,
 * aunque /auth/capabilities ya haya respondido (evita race PR #293/#294).
 */
export function shouldFetchSystemInfoForUser({ isPlatformDesigner, buyerPreviewActive = false }) {
  if (buyerPreviewActive && isPlatformDesigner) return true;
  return !isPlatformDesigner;
}

export function resolveEntitlementsHookLoading({
  isPlatformDesigner,
  buyerPreviewActive = false,
  systemInfoLoading,
  capabilitiesLoading,
  systemInfo,
}) {
  const skipPlatformBypass = buyerPreviewActive && isPlatformDesigner;
  if (isPlatformDesigner && !skipPlatformBypass) return false;
  if (systemInfoLoading) return true;
  if (capabilitiesLoading) return true;
  if (!systemInfo) return true;
  return false;
}

/**
 * Estado de entitlements para UI — prioriza capabilities solo cuando la carga consolidada terminó.
 * Durante loading evita usar capabilities parciales (race caps vs system/info).
 */
export function resolveEntitlementsState({
  effectivePlatformDesigner,
  loading,
  capabilitiesData,
  effectiveSystemInfo,
}) {
  if (effectivePlatformDesigner) {
    return { ...FULL_ENTITLEMENTS_STATE };
  }
  if (loading) {
    const parsed = entitlementsFromSystemInfo(effectiveSystemInfo);
    return { ...parsed, loading: true };
  }
  const fromCaps = entitlementsStateFromCapabilities(capabilitiesData);
  const fromInfo = entitlementsFromSystemInfo(effectiveSystemInfo);
  if (fromCaps) {
    // system/info (licencia instalada) puede autorizar módulos que capabilities
    // degradó; no bloquear UI si la licencia ya los incluye.
    return {
      ...fromCaps,
      entitlements: {
        ...fromCaps.entitlements,
        visor: Boolean(fromCaps.entitlements?.visor || fromInfo.entitlements?.visor || fromInfo.modulo_visor_sat),
        laboral: Boolean(fromCaps.entitlements?.laboral || fromInfo.entitlements?.laboral || fromInfo.modulo_asistente_laboral),
        tramites: Boolean(fromCaps.entitlements?.tramites || fromInfo.entitlements?.tramites || fromInfo.modulo_tramites_fiscales),
        despacho: Boolean(fromCaps.entitlements?.despacho || fromInfo.entitlements?.despacho || fromInfo.modulo_gestion_despacho),
      },
      modulo_visor_sat: Boolean(fromCaps.modulo_visor_sat || fromInfo.modulo_visor_sat),
      modulo_asistente_laboral: Boolean(fromCaps.modulo_asistente_laboral || fromInfo.modulo_asistente_laboral),
      modulo_tramites_fiscales: Boolean(fromCaps.modulo_tramites_fiscales || fromInfo.modulo_tramites_fiscales),
      modulo_gestion_despacho: Boolean(fromCaps.modulo_gestion_despacho || fromInfo.modulo_gestion_despacho),
      loading: false,
    };
  }
  return { ...fromInfo, loading: false };
}

/**
 * Hook centralizado — entitlements de instalación.
 * Fase 2: prioriza GET /auth/capabilities vía useCapabilities; fallback GET /system/info.
 *
 * TODO FASE 3: deprecar fallback system/info y este hook en favor de useCapabilities.
 */
export function useEntitlements() {
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const effectivePlatformDesigner = isPlatformDesigner && !buyerPreviewActive;
  const capabilities = useCapabilities();

  const [systemInfo, setSystemInfo] = useState(_sharedCache.data);
  const [systemInfoLoading, setSystemInfoLoading] = useState(
    shouldFetchSystemInfoForUser({ isPlatformDesigner, buyerPreviewActive }) && !_sharedCache.data,
  );

  const { previewSystemInfo } = usePlatformDesignerBuyerPreviewOrg({
    baseSystemInfo: systemInfo,
  });

  const effectiveSystemInfo = useMemo(() => {
    if (buyerPreviewActive && isPlatformDesigner && previewSystemInfo) {
      return previewSystemInfo;
    }
    return systemInfo;
  }, [buyerPreviewActive, isPlatformDesigner, previewSystemInfo, systemInfo]);

  const reload = useCallback(async () => {
    invalidateEntitlementsCache();
    setSystemInfoLoading(true);
    const [caps, info] = await Promise.all([
      capabilities.reload(),
      fetchSystemInfoShared(),
    ]);
    setSystemInfo(info);
    setSystemInfoLoading(false);
    return caps || info;
  }, [capabilities]);

  useEffect(() => {
    let cancelled = false;
    if (!shouldFetchSystemInfoForUser({ isPlatformDesigner, buyerPreviewActive })) {
      setSystemInfoLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setSystemInfoLoading((prev) => prev || !_sharedCache.data);
    fetchSystemInfoShared().then((data) => {
      if (!cancelled) {
        setSystemInfo(data);
        setSystemInfoLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isPlatformDesigner, buyerPreviewActive, user?.id]);

  const loading = resolveEntitlementsHookLoading({
    isPlatformDesigner,
    buyerPreviewActive,
    systemInfoLoading,
    capabilitiesLoading: capabilities.loading,
    systemInfo,
  });

  const state = useMemo(
    () =>
      resolveEntitlementsState({
        effectivePlatformDesigner,
        loading,
        capabilitiesData: capabilities.data,
        effectiveSystemInfo,
      }),
    [effectivePlatformDesigner, loading, capabilities.data, effectiveSystemInfo],
  );

  const checkEntitlement = useCallback(
    (entitlementId) =>
      hasEntitlement(entitlementId, state, { isPlatformDesigner: effectivePlatformDesigner }),
    [state, effectivePlatformDesigner],
  );

  const checkWorkModule = useCallback(
    (workModuleId) => {
      if (effectivePlatformDesigner) return true;
      return isWorkModuleEntitled(workModuleId, {
        entitlements: state.entitlements,
        modulePermissions: {
          modulo_visor_sat: state.modulo_visor_sat,
          modulo_asistente_laboral: state.modulo_asistente_laboral,
          modulo_tramites_fiscales: state.modulo_tramites_fiscales,
          modulo_gestion_despacho: state.modulo_gestion_despacho,
        },
      });
    },
    [state, effectivePlatformDesigner],
  );

  return {
    ...state,
    loading,
    isPlatformDesigner: effectivePlatformDesigner,
    buyerPreviewActive,
    systemInfo: effectiveSystemInfo,
    capabilities: capabilities.data,
    reload,
    hasEntitlement: checkEntitlement,
    isWorkModuleEntitled: checkWorkModule,
  };
}
